<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <b-input
              v-model="searchCode"
              :placeholder="
                $t('attributes.search') + $t('attributes.product_code')
              "
              type="search"
              icon="magnify"
              @input="onFilterStatus"
            ></b-input>
          </b-field>

          <b-field>
            <b-input
              v-model="searchEmployee"
              :placeholder="$t('attributes.search') + $t('attributes.employee')"
              type="search"
              icon="magnify"
              @input="onFilterStatus"
            ></b-input>
          </b-field>

          <b-field>
            <b-datepicker
              :placeholder="
                $t('message.please_select') + $t('attributes.date_range')
              "
              v-model="selectedDates"
              range
              nearby-selectable-month-days
              @input="onSelectedDates"
            >
              <button class="button is-primary" @click="onToday">
                <span>Today</span>
              </button>

              <button class="button is-info" @click="onLastWeek">
                <span>
                  {{ $t('message.lastWeek') }}
                </span>
              </button>
            </b-datepicker>
          </b-field>
        </b-field>
      </div>
      <div class="column is-1">
        <div class="buttons is-right">
          <b-button :loading="loading" class="is-primary" @click="onRefresh">
            {{ $t('message.refresh') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="loading"
      hoverable
      narrowed
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :current-page="parseInt(page)"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        width="340"
        :label="$t('attributes.id')"
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="employer_code"
        width="120"
        :label="$t('attributes.modifier')"
        v-slot="props"
      >
        {{ `[${props.row.employee_code}] ${props.row.employee_name}` }}
      </b-table-column>

      <b-table-column
        field="created_at"
        width="170"
        :label="$t('attributes.modify_date')"
        v-slot="props"
      >
        {{ formatDateTime(props.row.created_at) }}
      </b-table-column>

      <b-table-column
        width="120"
        :label="$t('attributes.ip_address')"
        v-slot="props"
      >
        {{ props.row.ip_address }}
      </b-table-column>

      <b-table-column
        field="service_name"
        width="200"
        :label="$t('attributes.function_name')"
        v-slot="props"
      >
        {{
          ControllerActionPath(props.row.controller_name, props.row.action_name)
        }}
      </b-table-column>

      <b-table-column
        field="service_name"
        width="200"
        :visible="false"
        :label="$t('attributes.service_name')"
        v-slot="props"
      >
        {{
          ServiceMethodPath(props.row.service_name, props.row.service_method)
        }}
      </b-table-column>

      <b-table-column
        width="160"
        :label="$t('attributes.activation_status')"
        v-slot="props"
      >
        <span>{{ shelveStatus(props.row.origin_data.shelve_on) }}</span>
        <b-icon icon="arrow-right-thick" size="is-small"></b-icon>
        <span>{{ shelveStatus(props.row.modified_data.shelve_on) }}</span>
      </b-table-column>

      <b-table-column
        field="resource_id"
        width="160"
        :label="$t('attributes.product_code')"
        v-slot="props"
      >
        {{ props.row.resource_id }}
      </b-table-column>

      <b-table-column
        field="resource_name"
        :label="$t('attributes.product_name')"
        v-slot="props"
      >
        <span v-html="props.row.resource_name"></span>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div v-if="!loading" class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>
  </section>
</template>

<script>
import { LOGS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'ProductStatus',

  data() {
    const { query } = this.$route
    const { startDate, endDate } = this.calculateDateRange(query)

    return {
      loading: false,
      total: 0,
      items: [],

      searchCode: query.code || '',
      searchEmployee: query.emp || '',
      sortField: query.sort || 'created_at',
      sortOrder: query.order || 'desc',
      defaultSortOrder: 'desc',
      page: query.page || 1,
      perPage: query.page_size || 10,

      selectedDates: [startDate, endDate],
      startDate,
      endDate
    }
  },
  mounted() {
    this.loadLogs()
  },
  beforeRouteUpdate(to, from, next) {
    if (to.name === from.name) {
      this.getQuery(to.query)
      this.loadLogs()
    }

    next()
  },
  methods: {
    async loadLogs() {
      try {
        const api = `${LOGS_API}/product-status`
        const params = {
          search_code: this.searchCode,
          search_employee: this.searchEmployee,
          start_date: this.startDate,
          end_date: this.endDate,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }
        this.loading = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('logs', e)
      } finally {
        this.loading = false
      }
    },
    getQuery(query) {
      this.searchCode = query.code || ''
      this.searchEmployee = query.emp || ''
      this.sortField = query.sort || 'created_at'
      this.sortOrder = query.order || 'desc'
      this.perPage = query.page_size || 10
      this.page = query.page || 1

      const { startDate, endDate } = this.calculateDateRange(query)
      this.startDate = startDate
      this.endDate = endDate
      this.selectedDates = [startDate, endDate]
    },
    updateQuery() {
      const query = {
        code: this.searchCode,
        emp: this.searchEmployee,
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString(),
        start: this.startDate && this.$dayjs(this.startDate).unix(),
        end: this.endDate && this.$dayjs(this.endDate).unix()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    calculateDateRange(query) {
      let startDate = null
      let endDate = null

      if (query.start && query.end) {
        startDate = this.$dayjs.unix(query.start).toDate()
        endDate = this.$dayjs.unix(query.end).toDate()
      } else {
        const today = this.$dayjs()
        startDate = today.subtract(7, 'day').toDate()
        endDate = today.toDate()
      }

      return { startDate, endDate }
    },
    onFilterStatus() {
      this.page = 1
      this.updateQuery()
    },
    onSelectedDates() {
      this.page = 1
      this.startDate = this.selectedDates[0]
      this.endDate = this.selectedDates[1]

      this.updateQuery()
    },
    onToday() {
      this.page = 1
      const today = this.$dayjs()
      this.startDate = today.toDate()
      this.endDate = today.toDate()
      this.selectedDates = [this.startDate, this.endDate]

      this.updateQuery()
    },
    onLastWeek() {
      this.page = 1
      const { startDate, endDate } = this.calculateDateRange({})
      this.startDate = startDate
      this.endDate = endDate
      this.selectedDates = [startDate, endDate]

      this.updateQuery()
    },
    onPageChange(page) {
      this.page = page

      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order

      this.updateQuery()
    },
    onRefresh() {
      this.loadLogs()
    }
  }
}
</script>
